/**
 * Détecte une annonce dont les données saisies par le concessionnaire sont
 * trop pauvres pour permettre une cotation ou une comparaison fiable
 * (ex. titre = juste la marque "Honda", aucun modèle renseigné).
 *
 * Ces annonces ne doivent PAS être affichées comme « exclusivité » ni mélangées
 * aux véhicules cotés : on les signale visuellement en rouge à l'utilisateur.
 *
 * @param {{titre?: string, model?: string, make?: string, version?: string}} a
 * @returns {{ incomplete: boolean, reasons: string[] }}
 */
export function getAnnonceCompletenessIssues(a) {
  if (!a) return { incomplete: false, reasons: [] }
  const titre = String(a.titre || '').trim()
  const model = String(a.model || '').trim()
  const make = String(a.make || '').trim()
  const version = String(a.version || '').trim()
  const reasons = []

  const titreLowered = titre.toLowerCase()
  const makeLowered = make.toLowerCase()

  // Cas 1 : aucun modèle renseigné en base (champ structurel manquant).
  if (!model) reasons.push('Modèle non renseigné')

  // Cas 2 : titre = strictement la marque (ex. "Honda" tout court).
  if (make && titreLowered === makeLowered) reasons.push('Titre identique à la marque')

  // Cas 3 : titre extrêmement court (≤ 6 caractères) sans modèle ni version.
  if (titre.length > 0 && titre.length <= 6 && !model && !version) {
    reasons.push('Libellé trop court')
  }

  // Cas 4 : titre vide ou seulement de la ponctuation.
  if (titre.replace(/[^a-z0-9]/gi, '').length === 0) reasons.push('Titre absent')

  return { incomplete: reasons.length > 0, reasons }
}

export function isAnnonceIncomplete(a) {
  return getAnnonceCompletenessIssues(a).incomplete
}

/**
 * Texte court à afficher dans les badges / sous-titres de listes.
 *
 * NB : on garde un libellé court (« Incomplète ») et pas « Annonce incomplète »
 * car le badge vit dans une colonne étroite (≈ 130 px) à côté de la valeur
 * « jours en stock ». La pastille rouge + la couleur rouge portent déjà le
 * signal visuel ; le bandeau d'en-tête et les panneaux de détail explicitent
 * « annonce incomplète » en entier pour l'utilisateur.
 */
export function shortIncompleteLabel() {
  return 'Incomplète'
}

/**
 * Phrase explicative à afficher dans les bandeaux / panneaux de détail.
 * @param {{ incomplete: boolean, reasons: string[] }} info
 */
export function describeIncompleteAnnonce(info) {
  if (!info?.incomplete) return ''
  const tail = info.reasons.length ? ` (${info.reasons.join(' · ')})` : ''
  return `Données insuffisantes pour coter ce véhicule${tail}. Complétez le titre / le modèle dans MotoScout pour obtenir une analyse fiable.`
}
